import { useEffect, useState } from "react";
import { AlertCircle, Power } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import BigToggle from "../../components/BigToggle";
import AdminShopPicker from "../../components/AdminShopPicker";

export default function AdminInventory() {
    const [rests, setRests] = useState([]);
    const [rid, setRid] = useState("");
    const [data, setData] = useState({ items: [], in_stock: 0, out_stock: 0, low_stock: 0 });
    const [activating, setActivating] = useState(false);

    useEffect(() => {
        api.get("/admin/restaurants").then(({ data }) => {
            const active = (data || []).filter((r) => r.status !== "INACTIVE" && r.status !== "DELETED");
            const sorted = [...active].sort((a, b) =>
                (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
            );
            setRests(sorted);
            if (sorted[0]) setRid(sorted[0].id);
        });
    }, []);

    const load = async (r = rid) => {
        if (!r) return;
        const { data } = await api.get(`/admin/inventory?restaurant_id=${r}`);
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

    const toggle = async (pid) => {
        try {
            await api.patch(`/admin/products/${pid}/toggle`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    // Clears the tenant-clone pipeline's backlog in one tap — cloned
    // shops AND cloned products both land INACTIVE for admin review;
    // activating the shop alone leaves customers looking at an empty
    // menu (see the "No active products" health-check flag on the
    // Shops page). Bulk-activates every INACTIVE product for the
    // currently-selected shop only, never the whole tenant, so it
    // can't accidentally revive items in unrelated shops.
    const inactiveCount = data.items.filter((p) => p.status === "INACTIVE").length;
    const activateAll = async () => {
        if (!rid || inactiveCount === 0) return;
        setActivating(true);
        try {
            const { data: res } = await api.post(`/admin/products/bulk-activate?restaurant_id=${rid}`);
            toast.success(`Activated ${res.activated} product${res.activated === 1 ? "" : "s"}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setActivating(false); }
    };

    const restaurant = rests.find((r) => r.id === rid);
    const tracking = !!restaurant?.track_inventory;

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Inventory</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Switch any item ON/OFF for a restaurant. Admin pauses are <span className="font-semibold">permanent</span> until you re-enable them (they do not auto-resume).
                    </p>
                </div>
                <AdminShopPicker
                    testIdPrefix="admin-inv-rest"
                    value={rid}
                    onChange={setRid}
                    shopsOverride={rests}
                />
            </div>

            {tracking && (
                <div className="grid grid-cols-3 gap-3 mt-6">
                    <Stat label="In stock" v={data.in_stock} klass="bg-emerald-100 text-emerald-700" />
                    <Stat label="Low stock (≤5)" v={data.low_stock} klass="bg-orange-100 text-orange-700" />
                    <Stat label="Out of stock" v={data.out_stock} klass="bg-red-100 text-red-700" />
                </div>
            )}

            {inactiveCount > 0 && (
                <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                        {inactiveCount} product{inactiveCount === 1 ? " is" : "s are"} inactive and hidden from customers in this shop.
                    </p>
                    <button
                        onClick={activateAll}
                        disabled={activating}
                        data-testid="admin-inv-activate-all"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3 py-2 disabled:opacity-60"
                    >
                        <Power size={13} /> Activate all
                    </button>
                </div>
            )}

            <div className="mt-6 bg-card border border-border rounded-2xl overflow-hidden">
                {/* Mobile + tablet portrait (<lg): stacked card view */}
                <div className="lg:hidden">
                    {data.items.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground text-sm">No items in this restaurant</div>
                    ) : data.items.map((p) => {
                        const isActive = p.status === "ACTIVE";
                        const isOutOfStock = tracking && p.stock != null && p.stock <= 0;
                        const isOff = !isActive || isOutOfStock;
                        const isLow = tracking && isActive && !isOutOfStock && p.stock != null && p.stock <= 5;
                        return (
                            <div key={p.id} data-testid={`admin-inv-card-${p.id}`} className="border-t border-border first:border-t-0 px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-sm inline-flex items-center gap-1">
                                            <span className="truncate">{p.name}</span>
                                            {isLow && <AlertCircle size={12} className="text-orange-600 shrink-0" />}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{inr(p.selling_price)}</div>
                                    </div>
                                    <BigToggle testid={`admin-tog-card-${p.id}`} on={!isOff} onClick={() => toggle(p.id)} />
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                                    <span><span className="uppercase tracking-wider font-bold text-[9px] text-muted-foreground/70">Status</span> · {p.status}</span>
                                    <span><span className="uppercase tracking-wider font-bold text-[9px] text-muted-foreground/70">Paused by</span> · {p.paused_by || "—"}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Desktop (lg+): classic table */}
                <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead>
                            <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                                <th className="px-4 py-3">Item</th>
                                <th className="px-4 py-3">Price</th>
                                <th className="px-4 py-3">Available</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Paused by</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((p) => {
                                const isActive = p.status === "ACTIVE";
                                const isOutOfStock = tracking && p.stock != null && p.stock <= 0;
                                const isOff = !isActive || isOutOfStock;
                                const isLow = tracking && isActive && !isOutOfStock && p.stock != null && p.stock <= 5;
                                return (
                                    <tr key={p.id} data-testid={`admin-inv-row-${p.id}`} className="border-t border-border">
                                        <td className="px-4 py-3 font-semibold">
                                            {p.name}
                                            {isLow && <AlertCircle size={12} className="inline ml-1 text-orange-600" />}
                                        </td>
                                        <td className="px-4 py-3">{inr(p.selling_price)}</td>
                                        <td className="px-4 py-3">
                                            <BigToggle testid={`admin-tog-${p.id}`} on={!isOff} onClick={() => toggle(p.id)} />
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{p.status}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{p.paused_by || "—"}</td>
                                    </tr>
                                );
                            })}
                            {data.items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No items in this restaurant</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, v, klass }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</p>
            <p className={`text-2xl font-bold mt-1 inline-block px-3 py-0.5 rounded-lg ${klass}`}>{v}</p>
        </div>
    );
}
